import { Mapping, useCustomEventDetailsQuery } from 'api/MappingsAPI';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { DEBOUNCE_TIMEOUTS_MS } from 'modules/customEvents/constants';
import { ReactNode, useMemo } from 'react';
import { useParams } from 'react-router';
import { formatFieldTypeForDisplay } from '../utils';
import { CustomEventDetailsContext } from './CustomEventDetailsContext';

export const CustomEventDetailsContextProvider = ({ children }: { children: ReactNode }) => {
  const { customEventName } = useParams<{ customEventName: string }>();
  const encodedName = encodeURIComponent(customEventName);

  const {
    data = [],
    error,
    isLoading: isLoadingCustomEventDetails,
    refetch,
  } = useCustomEventDetailsQuery(encodedName, {
    enabled: !!customEventName,
  });

  // format field type for consistent display between 'keyword' and 'string' values
  const customEventDetails = useMemo(
    () =>
      data.map((detail: Mapping) => ({
        ...detail,
        // remove custom event name because it's redundant
        fieldName: detail.fieldName.replace(`${customEventName}.`, ''),
        fieldType: formatFieldTypeForDisplay(detail.fieldType),
      })),
    [data, isLoadingCustomEventDetails],
  );

  const refetchCustomEventDetails = debounce(() => refetch(), DEBOUNCE_TIMEOUTS_MS.fetchMappings);

  const context = {
    customEventDetails,
    customEventDetailsError: error as AxiosError,
    isLoadingCustomEventDetails,
    refetchCustomEventDetails,
  };

  return (
    <CustomEventDetailsContext.Provider value={context}>
      {children}
    </CustomEventDetailsContext.Provider>
  );
};
